import React, { useState, useEffect } from 'react';
import { api } from '../api/client.js';
import { RefreshCw, Laptop, ShieldCheck, Terminal, CheckCircle2, Lock, Sparkles, Trash2, Plus, Edit, X } from 'lucide-react';

export const SuperAdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'platform' | 'admins' | 'devices' | 'audit' | 'subscriptions'>('platform');
  const [platformConfig, setPlatformConfig] = useState<any>(null);
  const [admins, setAdmins] = useState<any[]>([]);
  const [adminDevices, setAdminDevices] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Global Priority Subscriptions (All Shops)
  const [globalSubs, setGlobalSubs] = useState<any[]>([]);
  const [loadingGlobalSubs, setLoadingGlobalSubs] = useState(false);
  const [globalSubForm, setGlobalSubForm] = useState({ phone: '', label: '', days: 30 });
  const [globalSubSaving, setGlobalSubSaving] = useState(false);

  const loadGlobalSubs = async () => {
    setLoadingGlobalSubs(true);
    try {
      const data = await api.getGlobalSubscriptions();
      setGlobalSubs(data || []);
    } catch (err: any) {
      console.warn('Failed to load global subscriptions:', err?.message);
    } finally {
      setLoadingGlobalSubs(false);
    }
  };

  const handleAddGlobalSub = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!globalSubForm.phone.trim()) return;
    setGlobalSubSaving(true);
    try {
      await api.createGlobalSubscription({
        phone: globalSubForm.phone.trim(),
        label: globalSubForm.label.trim() || undefined,
        days: globalSubForm.days,
      });
      alert(`Global priority number ${globalSubForm.phone} enrolled successfully!`);
      setGlobalSubForm({ phone: '', label: '', days: 30 });
      await loadGlobalSubs();
    } catch (err: any) {
      alert(err?.message || 'Failed to enroll global subscriber');
    } finally {
      setGlobalSubSaving(false);
    }
  };

  const handleExtendGlobalSub = async (id: string, addDays: number) => {
    try {
      await api.updateGlobalSubscription(id, { addDays });
      await loadGlobalSubs();
    } catch (err: any) {
      alert(err?.message || 'Failed to extend global subscriber');
    }
  };

  const handleDeleteGlobalSub = async (id: string, phone: string) => {
    if (!confirm(`Delete global priority subscription for ${phone}?`)) return;
    try {
      await api.deleteGlobalSubscription(id);
      await loadGlobalSubs();
    } catch (err: any) {
      alert(err?.message || 'Failed to delete global subscriber');
    }
  };

  // New admin form
  const [newAdminUsername, setNewAdminUsername] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');

  // Edit admin modal state
  const [editingAdmin, setEditingAdmin] = useState<any | null>(null);
  const [editAdminUsername, setEditAdminUsername] = useState('');
  const [editAdminEmail, setEditAdminEmail] = useState('');
  const [editAdminPassword, setEditAdminPassword] = useState('');
  const [editAdminError, setEditAdminError] = useState<string | null>(null);
  const [savingEditAdmin, setSavingEditAdmin] = useState(false);

  const openEditAdmin = (admin: any) => {
    setEditingAdmin(admin);
    setEditAdminUsername(admin.username || '');
    const isInternal = admin.email && (admin.email.endsWith('.printbridge') || admin.email.endsWith('.local'));
    setEditAdminEmail(isInternal ? '' : (admin.email || ''));
    setEditAdminPassword('');
    setEditAdminError(null);
  };

  const handleSaveEditAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAdmin) return;
    setSavingEditAdmin(true);
    setEditAdminError(null);
    try {
      await api.updateAdminCredentials(editingAdmin.id, {
        username: editAdminUsername || undefined,
        email: editAdminEmail,
        password: editAdminPassword || undefined,
      });
      setEditingAdmin(null);
      const updated = await api.getAdmins();
      setAdmins(updated);
    } catch (err: any) {
      setEditAdminError(err.message || 'Failed to update administrator');
    } finally {
      setSavingEditAdmin(false);
    }
  };

  const sortDevices = (devs: any[]) => {
    return [...(devs || [])].sort((a, b) => {
      const dateA = new Date(a.approved_at || a.created_at || 0).getTime();
      const dateB = new Date(b.approved_at || b.created_at || 0).getTime();
      return dateB - dateA;
    });
  };

  const loadData = async () => {
    try {
      const [cfg, adm, logs, devs] = await Promise.all([
        api.getPlatformConfig(),
        api.getAdmins(),
        api.getAuditLogs(),
        api.getSuperDevices(),
      ]);
      setPlatformConfig(cfg);
      setAdmins(adm);
      setAuditLogs(logs);
      setAdminDevices(sortDevices(devs || []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updatePlatformConfig({
        diskCapGb: Number(platformConfig.disk_cap_gb),
        cleanupHighWatermarkPct: Number(platformConfig.cleanup_high_watermark_pct),
        cleanupTargetPct: Number(platformConfig.cleanup_target_pct),
        detailLogDays: Number(platformConfig.detail_log_days),
        finishedOrderRetentionDays: Number(platformConfig.finished_order_retention_days),
        defaultGraceDays: Number(platformConfig.default_grace_days),
        orderExpiryHours: Number(platformConfig.order_expiry_hours),
      });
      alert('Platform configuration updated!');
    } catch (err: any) {
      alert(err.message || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createAdmin({
        username: newAdminUsername,
        email: newAdminEmail || undefined,
        password: newAdminPassword,
      });
      setNewAdminUsername('');
      setNewAdminEmail('');
      setNewAdminPassword('');
      const updated = await api.getAdmins();
      setAdmins(updated);
      alert('Admin staff account created successfully!');
    } catch (err: any) {
      alert(err.message || 'Failed to create admin');
    }
  };

  const handleDeleteAdmin = async (adminId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete admin account "${name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await api.deleteAdmin(adminId);
      const updated = await api.getAdmins();
      setAdmins(updated);
      alert(`Admin "${name}" has been permanently deleted.`);
    } catch (err: any) {
      alert(err.message || 'Failed to delete admin');
    }
  };

  const handleToggleAdminDevice = async (deviceId: string, approved: boolean) => {
    try {
      await api.toggleDeviceApproval(deviceId, approved);
      const devs = await api.getSuperDevices();
      setAdminDevices(sortDevices(devs || []));
    } catch (err: any) {
      alert(err.message || 'Failed to toggle admin browser device approval');
    }
  };

  const handleDeleteAdminDevice = async (deviceId: string) => {
    if (!window.confirm('Are you sure you want to permanently revoke and delete this browser approval record? The device will be logged out immediately.')) {
      return;
    }
    try {
      // Step 1: Explicitly revoke first
      await api.toggleDeviceApproval(deviceId, false).catch(() => {});
      // Step 2: Permanently delete
      await api.deleteDevice(deviceId);
      const devs = await api.getSuperDevices();
      setAdminDevices(sortDevices(devs || []));
    } catch (err: any) {
      alert(err.message || 'Failed to delete browser device approval');
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-slate-400 flex items-center justify-center space-x-2">
        <RefreshCw className="w-5 h-5 animate-spin text-brand-400" />
        <span>Loading Super Admin controls...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight">Super Admin Platform Governance</h1>
        <p className="text-sm text-slate-400">
          Global resource caps, server health, admin staff accounts, and security audit log trail.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 p-1.5 rounded-2xl bg-white/5 border border-white/10 text-xs max-w-2xl">
        <button
          onClick={() => setActiveTab('platform')}
          className={`flex-1 py-2 px-3 rounded-xl font-semibold transition-all ${
            activeTab === 'platform' ? 'bg-brand-600 text-white shadow' : 'text-slate-400 hover:text-white'
          }`}
        >
          180 GB Cap & Rules
        </button>
        <button
          onClick={() => setActiveTab('admins')}
          className={`flex-1 py-2 px-3 rounded-xl font-semibold transition-all ${
            activeTab === 'admins' ? 'bg-brand-600 text-white shadow' : 'text-slate-400 hover:text-white'
          }`}
        >
          Admin Staff
        </button>
        <button
          onClick={() => {
            setActiveTab('devices');
            loadData();
          }}
          className={`flex-1 py-2 px-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-1.5 ${
            activeTab === 'devices' ? 'bg-brand-600 text-white shadow' : 'text-slate-400 hover:text-white'
          }`}
        >
          <Laptop className="w-3.5 h-3.5" />
          <span>Admin Browsers</span>
          {adminDevices.filter((d) => !d.approved).length > 0 && (
            <span className="px-1.5 py-0.2 rounded-full bg-amber-400 text-black font-bold text-[10px] animate-pulse font-mono">
              {adminDevices.filter((d) => !d.approved).length}
            </span>
          )}
        </button>
        <button
          onClick={() => {
            setActiveTab('subscriptions');
            loadGlobalSubs();
          }}
          className={`flex-1 py-2 px-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-1.5 ${
            activeTab === 'subscriptions' ? 'bg-brand-600 text-white shadow' : 'text-slate-400 hover:text-white'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-300" />
          <span>Global Priority</span>
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`flex-1 py-2 px-3 rounded-xl font-semibold transition-all ${
            activeTab === 'audit' ? 'bg-brand-600 text-white shadow' : 'text-slate-400 hover:text-white'
          }`}
        >
          Audit Logs
        </button>
      </div>

      {/* Tab: Platform & 180 GB Cap Config */}
      {activeTab === 'platform' && platformConfig && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 glass-panel p-6 rounded-3xl border border-white/10 space-y-5">
            <h3 className="text-base font-bold text-white">Storage Cap & Auto-Trim Thresholds</h3>
            <form onSubmit={handleSaveConfig} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 font-semibold mb-1">
                    Disk Safety Cap (GB)
                  </label>
                  <input
                    type="number"
                    value={platformConfig.disk_cap_gb}
                    onChange={(e) =>
                      setPlatformConfig({ ...platformConfig, disk_cap_gb: e.target.value })
                    }
                    className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-semibold mb-1">
                    High Watermark Trigger (%)
                  </label>
                  <input
                    type="number"
                    value={platformConfig.cleanup_high_watermark_pct}
                    onChange={(e) =>
                      setPlatformConfig({
                        ...platformConfig,
                        cleanup_high_watermark_pct: e.target.value,
                      })
                    }
                    className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 font-semibold mb-1">
                    Detail Log Retention (Days)
                  </label>
                  <input
                    type="number"
                    value={platformConfig.detail_log_days}
                    onChange={(e) =>
                      setPlatformConfig({ ...platformConfig, detail_log_days: e.target.value })
                    }
                    className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white"
                  />
                  <span className="text-[10px] text-slate-500">
                    Raw print_events kept 2 days, then aggregated to daily_stats.
                  </span>
                </div>
                <div>
                  <label className="block text-slate-300 font-semibold mb-1">
                    Finished Orders Retention (Days)
                  </label>
                  <input
                    type="number"
                    value={platformConfig.finished_order_retention_days}
                    onChange={(e) =>
                      setPlatformConfig({
                        ...platformConfig,
                        finished_order_retention_days: e.target.value,
                      })
                    }
                    className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 font-semibold mb-1">
                    Order Inactivity Auto-Expiry (Hours)
                  </label>
                  <input
                    type="number"
                    value={platformConfig.order_expiry_hours}
                    onChange={(e) =>
                      setPlatformConfig({ ...platformConfig, order_expiry_hours: e.target.value })
                    }
                    className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-semibold mb-1">
                    Default Subscription Grace Period (Days)
                  </label>
                  <input
                    type="number"
                    value={platformConfig.default_grace_days}
                    onChange={(e) =>
                      setPlatformConfig({ ...platformConfig, default_grace_days: e.target.value })
                    }
                    className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white"
                  />
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold shadow-md shadow-brand-500/20"
                >
                  {saving ? 'Saving...' : 'Save System Rules'}
                </button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-4 glass-panel p-6 rounded-3xl border border-white/10 space-y-4 text-xs">
            <h3 className="text-base font-bold text-white">Tiered Cap Trimming Order</h3>
            <ol className="space-y-2 list-decimal list-inside text-slate-300 leading-relaxed">
              <li>Temporary media cache</li>
              <li>Oldest print events (&gt; 2 days)</li>
              <li>Oldest finished order records</li>
              <li>Oldest daily rows rolled into monthly totals</li>
            </ol>
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-[11px]">
              <strong>Guaranteed:</strong> Live active orders, settings, credentials, and subscriptions are NEVER deleted.
            </div>
          </div>
        </div>
      )}

      {/* Tab: Admin Staff Management */}
      {activeTab === 'admins' && (
        <div className="space-y-6">
          <div className="glass-panel p-6 rounded-3xl border border-white/10 space-y-4">
            <h3 className="text-base font-bold text-white">Create New Administrator</h3>
            <form onSubmit={handleCreateAdmin} className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-xs">
              <input
                type="text"
                required
                placeholder="Username (e.g. admin_ops1)"
                value={newAdminUsername}
                onChange={(e) => setNewAdminUsername(e.target.value)}
                className="px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white font-mono"
              />
              <input
                type="email"
                placeholder="Email (Optional)"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                className="px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white"
              />
              <input
                type="password"
                required
                minLength={8}
                placeholder="Password (min 8)"
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                className="px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white"
              />
              <button
                type="submit"
                className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold"
              >
                Create Admin
              </button>
            </form>
          </div>

          <div className="glass-panel rounded-3xl border border-white/10 overflow-hidden">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="border-b border-white/10 text-slate-500 uppercase font-semibold bg-white/5">
                <tr>
                  <th className="py-3 px-4">Username</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Created At</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-white/5">
                    <td className="py-3 px-4 font-bold text-white font-mono">{admin.username || 'admin'}</td>
                    <td className="py-3 px-4 text-slate-400">{admin.email || 'N/A'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        admin.status === 'ACTIVE' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                      }`}>
                        {admin.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-400">
                      {new Date(admin.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right space-x-2">
                      <button
                        onClick={() => openEditAdmin(admin)}
                        className="px-2.5 py-1 rounded bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-[11px] font-semibold transition cursor-pointer"
                        title="Edit admin credentials, username, or email"
                      >
                        Edit Admin
                      </button>
                      <button
                        onClick={async () => {
                          const nextStatus = admin.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
                          await api.toggleAdminStatus(admin.id, nextStatus);
                          const updated = await api.getAdmins();
                          setAdmins(updated);
                        }}
                        className="px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 text-white text-[11px]"
                      >
                        {admin.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleDeleteAdmin(admin.id, admin.username || admin.email)}
                        className="px-2.5 py-1 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 text-[11px] font-semibold transition"
                        title="Permanently delete this administrator account"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* EDIT ADMIN MODAL */}
          {editingAdmin && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md">
              <div className="max-w-md w-full glass-card p-6 rounded-3xl border border-white/10 space-y-5 animate-scale-in">
                <div className="flex items-center justify-between pb-3 border-b border-white/10">
                  <div className="flex items-center space-x-2.5 text-white">
                    <div className="w-8 h-8 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center text-purple-400">
                      <Lock className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold">Edit Administrator</h3>
                      <p className="text-[11px] text-slate-400">Update admin username, email, or password</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingAdmin(null)}
                    className="p-1.5 rounded-xl hover:bg-white/10 text-slate-400 hover:text-white transition cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <form onSubmit={handleSaveEditAdmin} className="space-y-4 text-xs">
                  <div>
                    <label className="block text-slate-300 font-semibold mb-1">Username</label>
                    <input
                      type="text"
                      required
                      minLength={3}
                      value={editAdminUsername}
                      onChange={(e) => setEditAdminUsername(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 font-semibold mb-1">
                      Email Address <span className="text-purple-400 font-normal">(For Security OTPs & PINs)</span>
                    </label>
                    <input
                      type="email"
                      value={editAdminEmail}
                      onChange={(e) => setEditAdminEmail(e.target.value)}
                      placeholder="e.g. admin@company.com"
                      className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      💡 If provided, login OTP PINs will be sent here. If left blank, OTPs route to Super Admin email.
                    </p>
                  </div>

                  <div>
                    <label className="block text-slate-300 font-semibold mb-1">
                      Set New Password <span className="text-slate-500 font-normal">(Leave blank to keep current)</span>
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••••••"
                      value={editAdminPassword}
                      onChange={(e) => setEditAdminPassword(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500"
                    />
                  </div>

                  {editAdminError && (
                    <div className="p-3 rounded-xl bg-rose-500/20 border border-rose-500/30 text-rose-300 text-xs">
                      {editAdminError}
                    </div>
                  )}

                  <div className="flex items-center justify-end space-x-2 pt-2 border-t border-white/10">
                    <button
                      type="button"
                      onClick={() => setEditingAdmin(null)}
                      className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 text-xs font-semibold transition cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={savingEditAdmin}
                      className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold flex items-center space-x-1.5 transition disabled:opacity-50 cursor-pointer"
                    >
                      {savingEditAdmin ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      )}
                      <span>Save Changes</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Admin Staff Browser Approvals */}
      {activeTab === 'devices' && (
        <div className="space-y-4">
          <div className="glass-panel p-5 rounded-2xl border border-white/10 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-white flex items-center space-x-2">
                <Laptop className="w-5 h-5 text-emerald-400" />
                <span>Admin Staff Browser Authorizations</span>
              </h3>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                Super Admin Access Lock: Admin staff accounts cannot log in to manage shops until their browser is approved here by Super Admin. Super Admin credentials can be bootstrapped directly from the server.
              </p>
            </div>
            <button
              onClick={loadData}
              className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-semibold flex items-center space-x-1.5 transition"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh Authorizations</span>
            </button>
          </div>

          <div className="glass-panel rounded-3xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-300">
                <thead className="border-b border-white/10 text-slate-500 uppercase font-semibold bg-white/5">
                  <tr>
                    <th className="py-3 px-4">Admin Username</th>
                    <th className="py-3 px-4">Admin Email</th>
                    <th className="py-3 px-4">Browser Label / ID</th>
                    <th className="py-3 px-4">Last IP</th>
                    <th className="py-3 px-4">Accepted / Requested Date</th>
                    <th className="py-3 px-4">Last Seen</th>
                    <th className="py-3 px-4">Approval Status</th>
                    <th className="py-3 px-4 text-right">Access Control</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {adminDevices.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-slate-400">
                        No admin staff browser devices registered yet.
                      </td>
                    </tr>
                  ) : (
                    adminDevices.map((dev) => (
                      <tr key={dev.id} className="hover:bg-white/5 transition-colors">
                        <td className="py-3 px-4 font-bold text-white font-mono">
                          <div className="flex items-center gap-2">
                            <span>{dev.username || 'admin'}</span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono font-bold ${
                              dev.role === 'superadmin' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                            }`}>
                              {(dev.role || 'ADMIN').toUpperCase()}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-400">{dev.email || 'N/A'}</td>
                        <td className="py-3 px-4">
                          <div className="text-white font-medium">{dev.label || 'Web Browser'}</div>
                          <div className="text-[10px] text-slate-500 font-mono">ID: {dev.id.slice(0, 8)}...</div>
                        </td>
                        <td className="py-3 px-4 font-mono text-slate-300">{dev.last_ip || 'Local Network'}</td>
                        <td className="py-3 px-4">
                          {dev.approved && dev.approved_at ? (
                            <div>
                              <div className="text-emerald-400 font-medium">
                                {new Date(dev.approved_at).toLocaleString()}
                              </div>
                              <div className="text-[10px] text-slate-500">Approved</div>
                            </div>
                          ) : (
                            <div>
                              <div className="text-amber-400 font-medium">
                                {dev.created_at ? new Date(dev.created_at).toLocaleString() : 'Recent'}
                              </div>
                              <div className="text-[10px] text-slate-500">Requested</div>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-slate-400">
                          {dev.last_seen_at ? new Date(dev.last_seen_at).toLocaleString() : 'Never'}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                              dev.approved
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                : 'bg-amber-500/20 text-amber-300 border border-amber-500/40 animate-pulse'
                            }`}
                          >
                            {dev.approved ? 'APPROVED' : 'AWAITING APPROVAL'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            {dev.approved ? (
                              <button
                                type="button"
                                onClick={() => handleToggleAdminDevice(dev.id, false)}
                                className="px-3 py-1.5 rounded-lg bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-800/40 font-semibold text-xs transition cursor-pointer"
                              >
                                Revoke Access
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleToggleAdminDevice(dev.id, true)}
                                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md shadow-emerald-600/30 transition cursor-pointer"
                              >
                                Approve Browser
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteAdminDevice(dev.id)}
                              title="Delete Browser Approval"
                              className="px-2.5 py-1.5 rounded-lg bg-red-950/30 hover:bg-red-900/50 text-red-400 hover:text-red-200 border border-red-800/40 font-semibold text-xs transition cursor-pointer flex items-center space-x-1"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              <span>Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Audit Log Trail */}
      {activeTab === 'audit' && (
        <div className="glass-panel p-6 rounded-3xl border border-white/10 space-y-4">
          <h3 className="text-base font-bold text-white">System Audit Log Trail</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="border-b border-white/10 text-slate-500 uppercase font-semibold">
                <tr>
                  <th className="py-2.5 px-3">Timestamp</th>
                  <th className="py-2.5 px-3">Actor</th>
                  <th className="py-2.5 px-3">Role</th>
                  <th className="py-2.5 px-3">Action</th>
                  <th className="py-2.5 px-3">Summary</th>
                  <th className="py-2.5 px-3">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 font-mono text-[11px]">
                {auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-white/5">
                    <td className="py-2.5 px-3 text-slate-400 whitespace-nowrap">
                      {new Date(log.at).toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-white font-sans font-semibold">{log.who}</td>
                    <td className="py-2.5 px-3 uppercase text-[10px] text-slate-400">{log.role}</td>
                    <td className="py-2.5 px-3 text-brand-300 font-sans font-medium">{log.action}</td>
                    <td className="py-2.5 px-3 text-slate-300 font-sans max-w-xs truncate">{log.summary}</td>
                    <td className="py-2.5 px-3 text-slate-500">{log.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Global Priority Subscriptions (All Shops) */}
      {activeTab === 'subscriptions' && (
        <div className="glass-panel p-6 rounded-3xl border border-white/10 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <span>Global Priority Subscriber Numbers (Platform-wide)</span>
              </h3>
              <p className="text-xs text-slate-400 mt-1 max-w-2xl leading-relaxed">
                Orders sent from these numbers will automatically float to the top of the queue across <strong>ALL print shops</strong>.
                To shopkeepers, the order appears completely normal with no special badge, ensuring seamless and confidential VIP queue prioritization.
              </p>
            </div>
            <button
              type="button"
              onClick={loadGlobalSubs}
              className="px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-white text-xs font-semibold border border-white/10 transition-all flex items-center gap-1.5 self-start sm:self-auto cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingGlobalSubs ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          {/* Form to Add Global Subscriber */}
          <form onSubmit={handleAddGlobalSub} className="p-4 rounded-2xl bg-black/40 border border-amber-500/30 space-y-3.5">
            <div className="text-xs font-bold text-amber-300 uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Enroll Global Priority Customer Number</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
              <div className="sm:col-span-5">
                <label className="text-[11px] text-slate-400 font-medium block mb-1">Customer Phone Number</label>
                <input
                  type="tel"
                  value={globalSubForm.phone}
                  onChange={(e) => setGlobalSubForm({ ...globalSubForm, phone: e.target.value })}
                  placeholder="+91 94285 53298"
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/15 text-white font-mono text-xs focus:border-amber-400 focus:outline-none"
                  required
                />
              </div>

              <div className="sm:col-span-4">
                <label className="text-[11px] text-slate-400 font-medium block mb-1">VIP Label / Account Tag (Admin only)</label>
                <input
                  type="text"
                  value={globalSubForm.label}
                  onChange={(e) => setGlobalSubForm({ ...globalSubForm, label: e.target.value })}
                  placeholder="e.g. Master Executive Krish / University Partner"
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/15 text-white text-xs focus:border-amber-400 focus:outline-none"
                />
              </div>

              <div className="sm:col-span-3">
                <label className="text-[11px] text-slate-400 font-medium block mb-1">Duration</label>
                <select
                  value={globalSubForm.days}
                  onChange={(e) => setGlobalSubForm({ ...globalSubForm, days: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/15 text-white text-xs focus:border-amber-400 focus:outline-none font-medium"
                >
                  <option value={7}>7 Days (1 Week)</option>
                  <option value={15}>15 Days</option>
                  <option value={30}>30 Days (1 Month)</option>
                  <option value={60}>60 Days (2 Months)</option>
                  <option value={90}>90 Days (3 Months)</option>
                  <option value={180}>180 Days (6 Months)</option>
                  <option value={365}>365 Days (1 Year)</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                <span>Quick presets:</span>
                {[7, 30, 90, 365].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setGlobalSubForm({ ...globalSubForm, days: d })}
                    className={`px-2 py-0.5 rounded text-[10px] font-mono transition-all ${globalSubForm.days === d ? 'bg-amber-500 text-black font-bold' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
                  >
                    {d}d
                  </button>
                ))}
              </div>

              <button
                type="submit"
                disabled={globalSubSaving}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold text-xs flex items-center gap-1.5 shadow-md shadow-amber-500/20 transition-all cursor-pointer disabled:opacity-50"
              >
                {globalSubSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                <span>Activate Global Pass</span>
              </button>
            </div>
          </form>

          {/* List Table */}
          <div className="space-y-2.5">
            <div className="text-xs font-bold text-white flex items-center gap-1.5">
              <span>Active Global Numbers ({globalSubs.length})</span>
            </div>

            {loadingGlobalSubs ? (
              <div className="py-8 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-amber-400" />
                <span>Loading global priority subscribers...</span>
              </div>
            ) : globalSubs.length === 0 ? (
              <div className="p-8 rounded-2xl bg-black/20 border border-white/5 text-center space-y-2">
                <Sparkles className="w-6 h-6 text-slate-500 mx-auto" />
                <div className="text-xs text-slate-300 font-semibold">No Global Priority Numbers Configured</div>
                <div className="text-[11px] text-slate-500 max-w-sm mx-auto">
                  Add numbers above to grant them automatic #1 queue placement across all connected print shops.
                </div>
              </div>
            ) : (
              <div className="divide-y divide-white/5 border border-white/10 rounded-2xl overflow-hidden bg-black/20">
                {globalSubs.map((sub: any) => {
                  const isExpired = !sub.isActive || new Date(sub.validUntil).getTime() <= Date.now();
                  return (
                    <div key={sub.id} className="p-3.5 flex flex-wrap items-center justify-between gap-3 hover:bg-white/[0.02] transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isExpired ? 'bg-rose-500/10 text-rose-400' : 'bg-amber-500/15 text-amber-400'}`}>
                          <Sparkles className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm text-white font-mono">{sub.phone}</span>
                            {sub.label && (
                              <span className="text-xs px-2 py-0.5 rounded-md bg-white/5 text-slate-300 font-sans">
                                {sub.label}
                              </span>
                            )}
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold font-mono ${isExpired ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                              {isExpired ? '● EXPIRED' : `● ACTIVE (${sub.daysRemaining} days left)`}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono font-bold">
                              GLOBAL (ALL SHOPS)
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                            Valid until: {new Date(sub.validUntil).toLocaleDateString()} · Added {new Date(sub.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      {/* Actions: Extend & Delete */}
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleExtendGlobalSub(sub.id, 7)}
                          className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white text-xs font-semibold border border-white/10 transition-all cursor-pointer"
                          title="Add 7 days"
                        >
                          +7d
                        </button>
                        <button
                          type="button"
                          onClick={() => handleExtendGlobalSub(sub.id, 30)}
                          className="px-3 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-xs font-bold border border-amber-500/30 transition-all cursor-pointer flex items-center gap-1"
                          title="Add 30 days (1 month)"
                        >
                          <span>Extend +30d</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteGlobalSub(sub.id, sub.phone)}
                          className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs transition-all cursor-pointer"
                          title="Delete global priority pass"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
